import { useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useFeatures } from '../features/useFeatures';
import type { NavAuth } from '../navVisibility';

/**
 * The inputs `isNavItemVisible` needs, built in ONE place.
 *
 * The sidebar and the command palette each assembled this object by hand, with
 * a comment on the second saying the two must not drift. They already had once:
 * the palette carried its own copy of the visibility RULE and honoured only
 * `roles`, so every tenant user was offered the whole Platform section by name.
 * That was fixed by making `isNavItemVisible` the single decision — and left
 * the single decision reading two hand-written inputs, which is the same defect
 * one level down.
 *
 * Adding the partner gate is what made it bite: a fourth field added to one
 * caller and not the other hides the partner console from the palette while the
 * sidebar shows it, and nothing fails.
 */
export function useNavAuth(): NavAuth {
  const { role, user, assumedTenant, assumedPartner } = useAuth();
  const { features } = useFeatures();

  return useMemo(
    () => ({
      role,
      isPlatformAdmin: !!user?.isPlatformAdmin,
      hasAssumedTenant: assumedTenant !== null,
      features,
      /*
       * From `me.whiteLabels`, which the server already returns (step 6) and
       * which lists only partners the session actually administers — a
       * WL_VIEWER gets an empty array, so the client cannot offer a console the
       * guard then refuses.
       */
      /*
       * ⚠️ OR an ASSUMED one. A platform administrator holds no white-label membership
       * anywhere — the whole reason `WhiteLabelContextGuard` needed a branch for them — so
       * without this the server serves the console in full and the sidebar offers no link
       * to it. The fourth instance of the client being stricter than its server, after
       * `Can`'s private `hasRole`, the palette's private visibility rule and
       * `FeaturesService.list`.
       */
      administersWhiteLabel: (user?.whiteLabels ?? []).length > 0 || !!assumedPartner,
      /*
       * ⚠️ TRUE only when NO partner membership can write. One administering membership
       * restores the full menu — the same direction `resellerScoped` below resolves, and a
       * platform administrator who ENTERED a partner acts, so they are never read-only.
       */
      partnerReadOnly:
        !assumedPartner &&
        (user?.whiteLabels ?? []).length > 0 &&
        (user?.whiteLabels ?? []).every((wl) => wl.role === 'WL_VIEWER'),
      /*
       * ⚠️ `every`, so ONE partner-level membership restores the full menu. One
       * administering membership per person is structural today, so the question is
       * academic — but if that ever changes, somebody holding a partner-level
       * membership alongside a reseller-level one genuinely HAS the partner's
       * authority, and `some` would take their own console away. Err toward offering
       * a page the server would allow rather than hiding one it would.
       */
      /*
       * ⚠️ An ASSUMED reseller scope narrows the menu exactly as a real one does, and that
       * is the point of entering at that level: being shown the partner's brand and people
       * from inside a reseller's scope means looking at screens that administrator cannot
       * open, believing they are theirs. The server refuses them either way; this stops the
       * menu offering what it will refuse.
       */
      /*
       * ⚠️ TRUTHY, not `!== null`. A consumer that has not been given the field at all —
       * an older context, a test fixture built before it existed — yields `undefined`,
       * which is NOT null, so the strict check passed and the property access threw. The
       * same shape as a card reading a field the server has not sent yet: absent and
       * explicitly-null must behave alike, and only one of them does under `!==`.
       */
      resellerScoped: assumedPartner
        ? assumedPartner.resellerId !== null
        : (user?.whiteLabels ?? []).length > 0 &&
          (user?.whiteLabels ?? []).every((wl) => wl.resellerId !== null),
    }),
    // `features` arrives from its own query AFTER the first render. Left out of
    // a consumer's dependencies it froze the empty initial value, and the
    // palette then hid every feature-gated page the sidebar was showing.
    [role, user?.isPlatformAdmin, user?.whiteLabels, assumedTenant, assumedPartner, features],
  );
}
